import type { ComponentProps, ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { Trans } from '@lingui/react'

import { InlineButton } from '@twisto/components/atoms/button'
import { Tag, TagIcon, TagText } from '@twisto/components/atoms/tag'
import { CheckmarkOutlineIcon } from '@twisto/icons'

import { ResendButtonContent } from './components/resend-button-content'

type Props = {
  variant?: 'sms' | 'email'
  buttonProps?: Omit<
    ComponentProps<typeof InlineButton>,
    'children' | 'onClick'
  >
  children: ReactNode
  onClick: () => void
  actionAvailableAt?: string
  success?: boolean
  setSuccess: (value: boolean) => void
}

const smsResendDelay = 2000

export const ResendButton = ({
  buttonProps,
  children,
  onClick,
  actionAvailableAt,
  success = false,
  setSuccess,
  variant = 'sms',
}: Props) => {
  const [showTag, setShowTag] = useState(success)
  const [actionAvailable, setActionAvailable] = useState(false)

  useEffect(() => {
    setActionAvailable(false)
  }, [actionAvailableAt])

  useEffect(() => {
    setShowTag(success)

    if (success) {
      const id = setTimeout(() => {
        setSuccess(false)
      }, smsResendDelay)

      return () => clearTimeout(id)
    }
  }, [setSuccess, success])

  const handleClick = () => {
    onClick()
  }

  if (showTag) {
    return (
      <Tag variant="success">
        <TagIcon icon={CheckmarkOutlineIcon} />
        <TagText>
          {variant === 'sms' ? (
            <Trans id="resendSmsButton.success" />
          ) : (
            <Trans id="resendSmsButton.success.email" />
          )}
        </TagText>
      </Tag>
    )
  }

  return (
    <InlineButton
      data-testid="resend-button"
      {...buttonProps}
      disabled={!actionAvailable || buttonProps?.disabled}
      onClick={handleClick}
    >
      {!actionAvailable ? (
        <ResendButtonContent
          actionAvailableAt={actionAvailableAt}
          setActionAvailable={setActionAvailable}
          variant={variant}
        />
      ) : (
        children
      )}
    </InlineButton>
  )
}
